<!-- eslint-disable vue/prop-name-casing -->
<script setup>
import { orderBy } from 'lodash-es';
import { computed } from 'vue';
import { useModal } from 'vue-final-modal';
import HawkMembers from '~/common/components/organisms/hawk-members-badge/hawk-members.vue';
import { trainCase } from '~/common/utils/common.utils';
import { $date } from '~/common/utils/date.util';
import NumberTable from '~/terra/components/feature-details/num-table-modal.vue';
import { useBarcodeScanValidationHelper } from '~/terra/composables/barcode-scan-helper.composable.js';
import { useTerraStore } from '~/terra/store/terra.store.js';

const props = defineProps({
  is_feature_slider: {
    type: Boolean,
    default: false,
  },
  feature: {
    type: Object,
    default: null,
  },
  hide_quick_filter: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggleFilterHandler', 'checkStatusHandler']);

const { serializeIndicatorConfig } = useBarcodeScanValidationHelper();
const terra_store = useTerraStore();

const number_table_modal = useModal({
  component: NumberTable,
  attrs: {
    onClose() {
      number_table_modal.close();
    },
  },
});

const extraProperties = computed(() => {
  const feature = props.feature || terra_store.selected_features[0];
  const updated_feature = terra_store.features_hash[feature?.properties?.uid] || feature;

  if (!feature)
    return [];

  let { extraProperties } = updated_feature.properties;
  const additional_properties = terra_store.get_feature_additional_properties(updated_feature);
  extraProperties = {
    ...extraProperties,
    ...additional_properties,
  };
  if (!extraProperties)
    return [];
  return orderBy(Object.keys(extraProperties).filter(key => !terra_store.is_barcode_scan_map || key !== '_serial_numbers').map((key) => {
    return {
      name: key,
      value: extraProperties[key],
    };
  }), property => property.name?.toLowerCase(), ['asc']);
});

function openModal(val) {
  number_table_modal.patchOptions({
    attrs: {
      data: val,
    },
  });
  number_table_modal.open();
}

function getSelectedStatus(item) {
  return !terra_store?.filters_state?.key_values_data?.[item.name]?.[item.value]?.selected;
}

function toggleFilter(property) {
  const updated_selected_status = getSelectedStatus(property);
  terra_store.filters_state.key_values_data = {
    ...terra_store.filters_state.key_values_data,
    [property.name]: {
      ...(terra_store.filters_state.key_values_data[property.name] || {}),
      [property.value]: {
        selected: updated_selected_status,
      },
    },
  };
  if (!terra_store.filters_state.quick_filter && !updated_selected_status)
    terra_store.update_features_on_map_flag++;
  else
    terra_store.filters_state.quick_filter = updated_selected_status ? property : null;
}

emit('checkStatusHandler', item => getSelectedStatus(item));
emit('toggleFilterHandler', property => toggleFilter(property));
</script>

<template>
  <div
    v-for="(item, index) in extraProperties"
    :key="index"
    class="text-sm font-normal py-2"
  >
    <div class="flex text-sm group cursor-pointer">
      <div class="shrink-0 w-48 font-medium break-all mr-1" :class="is_feature_slider ? 'text-gray-500' : 'text-gray-700'">
        {{ trainCase(item.name) }}
      </div>
      <div class="flex-auto text-gray-900">
        <hawk-button v-if="item.name === '_serial_numbers'" type="link" class="!p-0 !h-auto" @click="openModal(item.value)">
          Show details
        </hawk-button>
        <div v-else-if="item.name === '_serial_numbers_last_updated_at'">
          {{ $date(item.value, 'D MMMM YYYY, h:mm a') }}
        </div>
        <div v-else-if="item.name === '_serial_numbers_last_updated_by'">
          <HawkMembers
            :members="item.value"
            type="badge"
          />
        </div>
        <div v-else-if="item.name === 'indicator_config'" class="w-full break-all">
          {{ serializeIndicatorConfig(item.value) }}
        </div>
        <div v-else-if="['disabled_modules', 'indicator_modules'].includes(item.name)" class="w-full break-all">
          {{ terra_store.get_modules_details(item.value, 'get') }}
        </div>
        <div v-else class="flex items-center justify-between">
          <span class="break-all">
            {{ item.value }}
          </span>
          <span v-if="!hide_quick_filter && item.value" :class="{ invisible: getSelectedStatus(item) }" class="group-hover:visible" @click="toggleFilter(item)">
            <IconHawkFilterFunnelOne v-if="getSelectedStatus(item)" v-tippy="$t('Add to filters')" class="w-4 h-4" />
            <IconHawkFilterFunnelRed v-else class="w-4 h-4" />
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
